import { z } from 'zod';
import { FLOW_TEST_WHOLE_DOCUMENT_POINTER, FlowTestJsonPointerSchema } from './jsonPointer.js';
import { FlowDurationSchema } from '../duration/duration.js';
export const FlowTestAssertionTargetKindSchema = z.enum([
    'BlockOutput',
    'BlockExecuted',
    'BlockError',
]);
export const FlowTestAssertionTargetKind = FlowTestAssertionTargetKindSchema.enum;
export const FlowTestAssertionTargetSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal(FlowTestAssertionTargetKind.BlockOutput),
        pointer: FlowTestJsonPointerSchema.default(FLOW_TEST_WHOLE_DOCUMENT_POINTER),
    }),
    /** No pointer: an unexecuted block has no output to point into. */
    z.object({ kind: z.literal(FlowTestAssertionTargetKind.BlockExecuted) }),
    z.object({
        kind: z.literal(FlowTestAssertionTargetKind.BlockError),
        pointer: FlowTestJsonPointerSchema.default(FLOW_TEST_WHOLE_DOCUMENT_POINTER),
    }),
]);
export const FlowTestAssertionComparatorKindSchema = z.enum([
    'Equals',
    'NotEquals',
    'Contains',
    'NotContains',
    'Exists',
    'NotExists',
    'GreaterThan',
    'LessThan',
    'DateWithin',
]);
export const FlowTestAssertionComparatorKind = FlowTestAssertionComparatorKindSchema.enum;
export const FlowTestAssertionComparatorSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal(FlowTestAssertionComparatorKind.Equals), value: z.unknown() }),
    z.object({ kind: z.literal(FlowTestAssertionComparatorKind.NotEquals), value: z.unknown() }),
    z.object({ kind: z.literal(FlowTestAssertionComparatorKind.Contains), value: z.unknown() }),
    z.object({ kind: z.literal(FlowTestAssertionComparatorKind.NotContains), value: z.unknown() }),
    z.object({ kind: z.literal(FlowTestAssertionComparatorKind.Exists) }),
    z.object({ kind: z.literal(FlowTestAssertionComparatorKind.NotExists) }),
    z.object({ kind: z.literal(FlowTestAssertionComparatorKind.GreaterThan), value: z.number() }),
    z.object({ kind: z.literal(FlowTestAssertionComparatorKind.LessThan), value: z.number() }),
    z.object({
        kind: z.literal(FlowTestAssertionComparatorKind.DateWithin),
        value: z.iso.datetime({ offset: true }),
        tolerance: FlowDurationSchema.refine((tolerance) => tolerance.value > 0, {
            path: ['value'],
            message: 'Date tolerance must be positive',
        }),
    }),
]);
const BaseFlowTestAssertionSchema = z.object({
    blockId: z.string().min(1),
    /** Shown in the verdict, so it should read as a sentence about the flow, not about the data. */
    name: z.string().min(1),
    target: FlowTestAssertionTargetSchema,
    comparator: FlowTestAssertionComparatorSchema,
});
/** Rejects target and comparator pairings that have no coherent meaning. */
export const FlowTestAssertionSchema = BaseFlowTestAssertionSchema.superRefine((assertion, context) => {
    const { target, comparator } = assertion;
    if (target.kind !== FlowTestAssertionTargetKind.BlockExecuted) {
        return;
    }
    if (comparator.kind !== FlowTestAssertionComparatorKind.Equals) {
        context.addIssue({
            code: 'custom',
            path: ['comparator', 'kind'],
            message: `A ${FlowTestAssertionTargetKind.BlockExecuted} assertion only supports ${FlowTestAssertionComparatorKind.Equals}`,
        });
        return;
    }
    if (typeof comparator.value !== 'boolean') {
        context.addIssue({
            code: 'custom',
            path: ['comparator', 'value'],
            message: `A ${FlowTestAssertionTargetKind.BlockExecuted} assertion compares against true or false`,
        });
    }
});
//# sourceMappingURL=assertion.js.map